import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import styles from './styles.module.less';
import { AddCsvFile } from '../../../../../../components/csv-import/ui/components/add-csv-file';
import { useImportContext } from '../../../../../../components/csv-import/ui/components/import-context';
import { ImportDescription } from '../../../../../../components/csv-import/ui/components/import-description';
import { InitErrors } from '../../../../../../components/csv-import/ui/components/init-errors/init-errors';
import { MappingList } from '../../../../../../components/csv-import/ui/components/mapping-list/mapping-list';
import { StepHeader } from '../../../../../../components/csv-import/ui/components/step-header/step-header';
import { StepSuspense } from '../../../../../../components/csv-import/ui/components/step-suspense/step-suspense';
import { SimpleDropdown } from '../../../../../../components/simple-dropdown';
import { linkIDs } from '../../../../../../services/link-service/links';
import Global from '../../../../../../store/global-selectors';
import { WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';
import { INVENTORY_WAREHOUSE } from '../reducer';
import type { InventoryWarehouse, PurchaseOrderForm } from '../types';

const exampleFile = '/s3/marketplaces/shipstation/PurchaseOrderImportSample.csv';

export const ConfigStepBody = () => {
  const { dispatch, isGetMappingsLoading, isInitImportLoading, form, trackProcess } = useImportContext();
  const warehouseLocations = useSelector(Global.selectSellerInventoryWarehouses);
  const typedForm = form as PurchaseOrderForm;

  const changeInventoryWarehouse = useCallback(
    (inventoryWarehouseId: string) => {
      dispatch({
        type: INVENTORY_WAREHOUSE,
        inventoryWarehouse: inventoryWarehouseId,
      });

      trackProcess(WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY, {
        property: 'inventoryWarehouseId',
        value: inventoryWarehouseId,
      });
    },
    [dispatch, trackProcess],
  );

  const cachedWarehouseLocations = useMemo(() => {
    return warehouseLocations.map((inventoryWarehouse: InventoryWarehouse) => ({
      key: inventoryWarehouse.inventoryWarehouseId,
      name: inventoryWarehouse.name,
    }));
  }, [warehouseLocations]);

  return (
    <StepSuspense isLoading={isGetMappingsLoading || isInitImportLoading}>
      <StepHeader>
        <FormattedMessage {...messages.uploadFile} />
      </StepHeader>
      <ImportDescription articleId={linkIDs.ImportCsvPurchaseOrder} file={exampleFile} message={messages.helpContentText} />
      <InitErrors />
      <AddCsvFile name='purchase-order-import' />
      <FlexRow className={styles['dropdown-container']}>
        <FlexColumn className={styles['dropdown-wrapper']}>
          <Text className={styles['dropdown-title']} textType={TextType.Label} value={messages.selectInventoryWarehouseHeader} />
          <div className={styles['dropdown-menu']}>
            <SimpleDropdown
              defaultTitle={<FormattedMessage {...messages.selectInventoryWarehouse} />}
              dropdownMenuShouldHaveWidthOfButton
              items={cachedWarehouseLocations}
              onItemSelected={changeInventoryWarehouse}
              selectedItemKey={typedForm.inventoryWarehouse}
            />
          </div>
        </FlexColumn>
        <FlexColumn className={styles['dropdown-wrapper']}>
          <MappingList menuClassName={styles['dropdown-menu']} />
        </FlexColumn>
      </FlexRow>
    </StepSuspense>
  );
};
